/**
 * Step `route_study` (SA-1198, spec §5.1): the ids the view binds to, its
 * contract check and its reads of the task. Pure: no React, no fetch, because
 * the manifest imports `matchesRouteStudy` eagerly. It never reads the pin text
 * (SA-859 D5): the base is sent as typed and the server's `parse_reference` reads it.
 */
import type { TaskRead } from '@/types/process'
import { carries } from '../contract'
import { F_LINK, F_REFERENCE } from './pickerModel'
import type { AuthoredSectorIn } from './types'

export const STEP_ROUTE_STUDY = 'route_study'
export const EXIT_DONE = 'done'
export const NEED_SECTORS = 'sectors'
export const NEED_AIRCRAFT = 'aircraft_type'
export const F_NOTES = 'airspace_permit_notes'
export const F_TECH_STOP = 'technical_stop_option'

/** The view can do its job: the pin's two fields on the form as strings, `done` offered, the
 * sectors among the needs (plus `carries`' rules: nothing redacted, no AI proposal). A record
 * pinned to the old step 6 (no `study_reference` on its form) keeps the generated form. */
export function matchesRouteStudy(task: TaskRead): boolean {
  if (!carries(task, [F_REFERENCE, F_LINK], [EXIT_DONE])) return false
  const typeOf = (id: string) => task.form.find((f) => f.id === id)?.type
  return typeOf(F_REFERENCE) === 'string' && typeOf(F_LINK) === 'string'
    && task.needs.some((n) => n.id === NEED_SECTORS)
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** The base chosen at step 5, as typed: the form's pre-fill, else the need's value; trimmed. */
export function baseTextOf(task: TaskRead): string | null {
  const field = task.form.find((f) => f.id === F_REFERENCE)
  return text(field?.prefill?.value) ?? text(task.needs.find((n) => n.id === F_REFERENCE)?.value)
}

/** Step 4's sectors as the pre-fill route takes them, the payload in tonnes; a row with a station
 * missing is kept (the editor refuses the save on it, spec Review Focus). */
export function sectorsOf(task: TaskRead): AuthoredSectorIn[] {
  const value = task.needs.find((n) => n.id === NEED_SECTORS)?.value
  if (!Array.isArray(value)) return []
  return value.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object').map((row) => ({
    from: typeof row.from === 'string' ? row.from : null,
    to: typeof row.to === 'string' ? row.to : null,
    flown_as: typeof row.flown_as === 'string' ? row.flown_as : null,
    payload: typeof row.payload === 'number' && Number.isFinite(row.payload) ? row.payload : null,
  }))
}

/** The rotation's aircraft type as typed upstream, trimmed (the cost sheet's own rule); blank, absent or not text is none. */
export function aircraftTypeOf(task: TaskRead): string | null {
  return text(task.needs.find((n) => n.id === NEED_AIRCRAFT)?.value)
}

/** Where the grid in progress is kept (spec A12). */
export function browserCopyKey(taskId: string): string {
  return `route-study:${taskId}`
}
